// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @typescript-eslint/naming-convention */
// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import { unescape } from "lodash-es";

export const resizeObserver = (callback: () => void): ResizeObserver =>
  new ResizeObserver((entries) => {
    // We wrap it in requestAnimationFrame to avoid this error - ResizeObserver loop limit exceeded.
    window.requestAnimationFrame(() => {
      if (!Array.isArray(entries) || entries.length === 0) {
        return;
      }
      callback();
    });
  });

export const isAnchorItem = (
  item: unknown,
): item is HTMLQdsWebAnchorElement => {
  if (!(item instanceof HTMLElement)) return false;
  const tagName = item.tagName.toLowerCase();

  return (
    (!item.inert && tagName === "qds-anchor") ||
    (item as HTMLQdsWebAnchorElement).disabled !== true
  );
};

export const isNumber = (number: number | string): boolean =>
  Number.isInteger(number) || /^\d*$/.test(number as string);

export const isTouchDevice = (): boolean =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0;

export const parseValue = (valueToParse: string): string[] | undefined => {
  try {
    return JSON.parse(unescape(valueToParse)) as string[];
  } catch {
    if (valueToParse.split(";").length > 0) return valueToParse.split(";");
    // eslint-disable-next-line no-console
    console.error(
      "Couldn't JSON parse. Stringify the object or use `;` to separate different values if the object is a list of strings",
    );
    return undefined;
  }
};

export const parseNumberArray = (
  valueToParse: string,
  componentTag: string,
  propertyName: string,
): number[] | undefined => {
  try {
    const parsedItemsPerPageOptions = JSON.parse(valueToParse) as number[];

    if (
      Array.isArray(parsedItemsPerPageOptions) &&
      parsedItemsPerPageOptions.every((item) => typeof item === "number")
    ) {
      return parsedItemsPerPageOptions;
    }
    return undefined;
  } catch {
    // eslint-disable-next-line no-console
    console.error(
      `[${componentTag}] Invalid '${propertyName}' value. ` +
        `You must provide a stringified array of numbers, e.g. '[2,4,8]'. ` +
        `Received: ${String(valueToParse)}`,
    );
    return undefined;
  }
};

export const randomId = (): string =>
  `noid_${Math.random().toString(36).slice(2, 11)}`;

export const convertToBold = (text: string): string =>
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  text.replace(/\*\*(.*?)\*\*/g, (_, word) => `<strong>${word}</strong>`);

export const convertToGreen = (text: string): string =>
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  text.replace(
    /\*\*(.*?)\*\*/g,
    (_, word) =>
      `<span style="color: var(--qds-theme-signature-color-default)">${word}</span>`,
  );

export const cleanText = (text: string): string =>
  // eslint-disable-next-line unicorn/prefer-string-replace-all
  text.replace(/\*\*(.*?)\*\*/g, "$1");

export const getBokeccVideoId = (
  url: string,
): { videoId: string; siteId: string } => {
  const regExp =
    /(?:https?:\/\/)?(?:www\.)?(?:p\.)?bokecc\.com\/(?:playhtml\.bo|player\.bo)\?vid=([\dA-Fa-f]{32})(?:&siteid=([\dA-Fa-f]+))?/;
  const match = regExp.exec(url);

  return {
    videoId: match?.[1] ?? "",
    siteId: match?.[2] ?? "",
  };
};

export const getYoutubeVideoId = (url: string): string => {
  const regExp =
    /(?:.+?)?(?:\/v\/|watch\/|\\?v=|\\&v=|youtu\\.be\/|\/v=|^youtu\\.be\/|watch\\%3Fv\\%3D)([\w-]{11})+/;
  const secondaryRegExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = regExp.exec(url) ?? secondaryRegExp.exec(url);

  return match?.[7] ?? match?.[1] ?? "";
};

export const getYoukuVideoId = (url: string): string => {
  const regExp =
    /(?:https?:\/\/)?(?:www\.)?(?:v|player)\.youku\.com\/(?:v_show\/id_|embed\/)(\w+)/;
  const match = regExp.exec(url);

  return match?.[1] ?? "";
};

export const getSketchfabModelId = (url: string): string => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?sketchfab\.com\/models\/([\da-f]{32})|api\.sketchfab\.com\/v3\/models\/([\da-f]{32})/;
  const match = regex.exec(url);

  return match?.[1] ?? match?.[2] ?? "";
};

export const getResponsiveImageSource = (
  imageSource: string,
): { computedSrcSet: string; computedSrc: string } => {
  const trimmedSource = imageSource.trim();
  let computedSource = trimmedSource;
  let computedSourceSet = "";

  if (trimmedSource.includes(" ")) {
    [computedSource] = trimmedSource.split(" ");
    computedSourceSet = trimmedSource;
  }

  return {
    computedSrc: computedSource,
    computedSrcSet: computedSourceSet,
  };
};

/**
 * When inside a double-quoted attribute value, only `&` and `"` hold special meaning.
 * @see https://html.spec.whatwg.org/multipage/parsing.html#attribute-value-(double-quoted)-state
 */
const escapeHtmlAttributeDict: Record<string, string> = {
  "&": "&amp;",
  '"': "&quot;",
  // Svelte also escapes < because the escape function could be called inside a `noscript` there
  // https://github.com/sveltejs/svelte/security/advisories/GHSA-8266-84wp-wv5c
  // However, that doesn't apply in SvelteKit
};

/**
 * @type {Record<string, string>}
 */
const escapeHtmlDict: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
};

const surrogates =
  // high surrogate without paired low surrogate
  "[\\ud800-\\udbff](?![\\udc00-\\udfff])|" +
  // a valid surrogate pair, the only match with 2 code units
  // we match it so that we can match unpaired low surrogates in the same pass
  // TODO: use lookbehind assertions once they are widely supported: (?<![\ud800-udbff])[\udc00-\\udfff]
  "[\\ud800-\\udbff][\\udc00-\\udfff]|" +
  // unpaired low surrogate (see previous match)
  "[\\udc00-\\udfff]";

const escapHtmlAttributeRegex = new RegExp(
  `[${Object.keys(escapeHtmlAttributeDict).join("")}]|${surrogates}`,
  "g",
);

const escapeHtmlRegex = new RegExp(
  `[${Object.keys(escapeHtmlDict).join("")}]|${surrogates}`,
  "g",
);

/**
 * Escapes unpaired surrogates (which are allowed in js strings but invalid in HTML) and
 * escapes characters that are special.
 *
 * @param {string} str
 * @param {boolean} [is_attr]
 * @returns {string} escaped string
 * @example const html = `<tag data-value="${escapeHtml('value', true)}">...</tag>`;
 */
export const escapeHtml = (string_: string, isAttribute?: boolean): string => {
  const dict = isAttribute ?? false ? escapeHtmlAttributeDict : escapeHtmlDict;

  return string_.replace(
    isAttribute ?? false ? escapHtmlAttributeRegex : escapeHtmlRegex,
    (match) => {
      if (match.length === 2) {
        // valid surrogate pair
        return match;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return dict[match] ?? `&#${match.codePointAt(0)};`;
    },
  );
};

export const triggerSlotChange = (
  shadowRoot: ShadowRoot,
  handler: (event: Event) => void,
): void => {
  const slots = shadowRoot.querySelectorAll("slot");
  for (const slot of slots) {
    if (slot instanceof HTMLSlotElement) {
      handler({ target: slot } as unknown as Event);
    }
  }
};
